import punycode from 'punycode/';
import storage from 'local-storage-fallback';

import { COOKIE_NAMES, MAIL, STORAGE_KEY_NAMES } from 'newJs/config';
import { getAddressName } from 'newJs/utils/emlHeaders';
import biscuit from 'tbSrc/js/utils/cookie-handler';
import templates from './htmlTemplates';
import composeSelectors from 'containers/Compose/utils/composeSelectors';
import { parseMultipleRawEmails } from '../../../../utils/email';
import { fixInlineImages } from '../../../utils/inlineImage';

const {
    ACCEPTED_MAILTO_FIELDS: AMF,
    MODE_NAMES: CMN,
    TYPE_NAMES: CTN
} = MAIL.get('COMPOSE');
const { COMPOSE_CACHE } = STORAGE_KEY_NAMES;
const DF = MAIL.get('DATE_FORMATS');
const MHF = MAIL.get('HEADER_FIELDS');
const SENSITIVE_WORDS_COOKIE = COOKIE_NAMES.get('SHOW_SENSITIVE_WORDS_WARNING');
const bodyHtml = templates.get('compose');

const format = {
    address({ address, personal }) {
        const name = personal && personal.length > 0 ? personal : '';
        return `${name} &lt;<a href="mailto:${address}">${punycode.toUnicode(address)}</a>&gt;`;
    },
    body(data = {}, { i18n, mode, user }) {
        // console.log('data', data);
        if (mode === CMN.COMPOSE) return '';

        const { message } = data;
        if (Object.keys(data).length < 1 || Object.keys(message).length < 1) return '';

        const format = this;
        let body = message.body;

        /** Replace all "cid:" in the email body with MAPI url
         * @see [MAIL-5509] for more info
        */
        if ([CMN.DRAFT, CMN.FORWARD, CMN.REPLY].includes(mode)) {
            //todo maybe just html entity decode whole message?
            //need to ask mapi devs and android/ios devs why it was entity encoded in the first place...

            body = fixInlineImages(body);

            //fix partid wrongly encoded (removes + sign)
            body = body.replace(/\?partid&#61;&#43;/, '?partid&#61;');
        }

        //remove NUL characters
        body = body.replace(/\0/g, '');

        if (mode === CMN.DRAFT) return body;

        const from = getAddressName(data.from[0]);
        // [MAIL-5591]: There are rare cases when 'to' field is empty
        const toAddress = (data?.to?.length > 0)
            ? data.to[0]
            : { address: user.email, personal: user.name };
        const to = getAddressName(toAddress);
        const subject = data.subject.length > 0
            ? data.subject
            : i18n.noSubject;
        const msgDateTime = [CMN.FORWARD, CMN.REPLY].includes(mode)
            ? message.receivedDateTime
            : message.dateTime;
        const date = format.date({
            locale: user.locale,
            timeString: msgDateTime,
            timeZone: user.timeZone
        });

        return bodyHtml(mode, {
            body, date, subject,
            from: format.address(from),
            to: format.address(to),
        });
    },
    date({ locale, timeString, timeZone }) {
        const dateTime = timeString ? new Date(timeString) : new Date();
        const timeZoneDef = timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;
        return dateTime.toLocaleString(locale, { timeZone: timeZoneDef, ...DF.forEmlCompose });
    },
    subject({ subject, mode, noSubjectText }) {
        const prefix = mode === CMN.FORWARD ? 'Fwd: ' : 'Re: ';
        return prefix + (subject.length > 0 ? subject : noSubjectText);
    }
};

/* Remap data for autocomplete plugin (fields: to, cc, bcc, replyTo).
    Input object from MAPI response:
    {
        "to": [{ "address": "testdev1@inbox.lv", "personal": "Testdev1" }],
        "cc": [...],
        "bcc": [...],
    }
    Output object for the jQuery autocomplete plugin:
    {
        "to": [{
            displayName: '"testdev1" <testdev1@inbox.lv>',
            name: 'testdev1',
            email: 'testdev1@inbox.lv'
        }],
        "cc": [...],
        "bcc": [...],
    }
*/
const remapForAutocomplete = (list = {}) => {
    const listKeys = Object.keys(list);
    if (listKeys.length < 1) return {};

    const result = listKeys.reduce((acc, item) => {
        if (list[item].length < 1) {
            acc[item] = [];
            return acc;
        }
        const addresses = list[item].map((item) => {
            const personal = item?.personal?.length > 0 ? item.personal.trim() : '';
            const email = punycode.toUnicode(item.address);
            const name = personal.length > 0 ? punycode.toUnicode(personal) : email;
            const displayName = name.length > 0 ? `"${name}" <${email}>` : email;

            return { displayName, name, email: item.address };
        });
        acc[item] = addresses;
        return acc;
    }, {});
    // console.log('input:\n', list, '\n output:\n', result, '\n====================');
    return result;
};

const isBase64 = str => {
    try {
        return btoa(atob(str)) === str;
    } catch (err) {
        return false;
    }
};

export default {
    remapForAutocomplete,

    deleteSavedFormData: (user) => {
        const cacheKey = `${user.email}_${COMPOSE_CACHE}`;
        storage.removeItem(cacheKey);
        storage.removeItem('inboxWordAlertHasWords');
    },

    extractEmlHeaders: (data = {}) => {
        if (Object.keys(data).length < 1) return {};
        // Need to insert 'replyTo' field here, because it is not used in other places so far
        const names = Object.values(MHF);
        names.splice(-1, 0, 'replyTo');

        return names.reduce((acc, item) => {
            if (Array.isArray(data[item]) && data[item].length < 1) return acc;
            acc[item] = data[item];
            return acc;
        }, {});
    },

    formatEmlBody: format.body.bind(format),

    formatEmlSubject: format.subject,

    getDataForHeaderAutocomplete: (data) => {
        /** NOTE
            Addresses or a groupId can be passed from Inbox Contacts,
            use 'prefill' addresses in this case
            Take groupId from the URL search params instead of prefill,
            since they are the same thing
        */
        const searchParams = new URLSearchParams(window.location.search);
        const composeFromContactsGroup = searchParams.has(CTN.TO_GROUP) && searchParams.get(CTN.TO_GROUP).trim().length > 0;

        return {
            addresses: remapForAutocomplete(data),
            ...(composeFromContactsGroup && { toGroupId: searchParams.get(CTN.TO_GROUP) })
        };
    },
    // Get TO, CC, BCC, SUBJECT and BODY from the location.search
    getEmlDataFromUrl: async () => {
        const searchParams = new URLSearchParams(window.location.search);
        const notEncoded = [AMF.SUBJECT, AMF.BODY];

        return Object.values(AMF).reduce(async (accPromise, key) => {
            let acc = await accPromise;
            let value = null;
            if (searchParams.has(key)) {
                value = searchParams.get(key);
                if (!value) {
                    return acc;
                }
                if (notEncoded.includes(key)) {
                    //subject and body are used "as is"
                    acc[key] = value;
                } else {
                    //email fields (to,cc,bcc) field have special parsing
                    // email query params can originate from:
                    // * dating
                    // * on reply,reply-all
                    // * inside message view as <a href="mailto:test@example.com"></a> links
                    // * and other places
                    //first we get actual value
                    if (key === AMF.CONTACT_LIST_ID) {
                        //this is contact list from contacts project
                        value = sessionStorage.getItem('contactList_' + value);
                        key = AMF.TO;
                    }
                    //emails should always be passed in base64 but for now we support also non-base64
                    if (isBase64(value)) {
                        value = atob(value);
                    }
                    value = decodeURIComponent(value);
                    //now when we have value we need to parse it:
                    // * it can be multiple emails separated by comma
                    // * personal part is optional
                    // Parse multiple emails (using the async function)
                    const emails = await parseMultipleRawEmails(value);
                    acc[key] = acc[key] || [];
                    emails.forEach((item) => {
                        const { personal, address } = item;
                        acc[key].push({
                            personal: personal,
                            address: punycode.toUnicode(address),
                        });
                    });
                }
            }
            return acc;
        }, Promise.resolve({})); // Start with a resolved promise as the initial value for reduce
    },

    getDefaultIdentity: (obj = {}) => {
        // console.log(obj);
        const skeleton = { current: 0, params: {} };
        if (Object.keys(obj).length < 1) return skeleton;

        const { default: current, list } = obj;
        return { ...skeleton, current, params: list[current] };
    },

    getModeFromPathName: () => {
        const pathname = document.location.pathname.substring(1);
        const values = Object.values(CMN).join('|');
        const regex = new RegExp(`^(${values})$`);
        const match = regex.exec(pathname);

        return match ? match[0] : CMN.COMPOSE;
    },

    isFetchEnabledForMode: (mode) => {
        const enabledModes = [CMN.DRAFT, CMN.REPLY, CMN.REPLY_ALL, CMN.FORWARD];
        return enabledModes.includes(mode);
    },

    isHeaderValueEmpty: (idString) => {
        const elm = document.getElementById(idString);
        return elm === null ? null : elm.value.trim().length < 1;
    },

    isHeaderValuesEmpty() {
        const withoutFrom = Object.values(MHF).filter((item) => item !== MHF.FROM);
        return withoutFrom.every((name) => this.isHeaderValueEmpty(name));
    },

    prepareCkedParams: (args) => {
        // console.log(args);
        const { currentMode, editorOptions, i18n, params, project, user } = args;
        const { identities } = params.compose;
        const { locale } = user;

        return {
            cked: {
                ...editorOptions,
                ...params.cked,
                selectors: {
                    formId: composeSelectors.composeFormId,
                    messageId: composeSelectors.message,
                },
                language: locale,
                mode: {
                    isDraft: CMN.DRAFT === currentMode,
                    isForward: CMN.FORWARD === currentMode,
                    isReply: CMN.REPLY === currentMode,
                }
            },
            other: {
                i18n, identities, user,
                assetsPath: project.path.assets,
            }
        };
    },

    removeHeaderValue: (name) => {
        // Used jQuery here since autocomplete is jQery plugin
        const $elm = $(`#suggest-${name}`);
        // 'reset' is a custom event for autocomplete
        $elm.trigger('reset');
    },

    showSensitiveWordsModal: () => {
        const hasCookie = Boolean(biscuit.getIt(SENSITIVE_WORDS_COOKIE)) && true;
        let hasSensitiveWords = false;
        if (storage.getItem('inboxWordAlertHasWords')!== null) {
            hasSensitiveWords = JSON.parse(storage.getItem('inboxWordAlertHasWords'));
        }
        return (!hasSensitiveWords || hasCookie) ? false : true;
    },

    toggleShowModalCookie: (toggle) => {
        if (typeof toggle !== 'boolean') {
            return biscuit.getIt(SENSITIVE_WORDS_COOKIE);
        }
        if (toggle) {
            biscuit.setIt(SENSITIVE_WORDS_COOKIE, 1);
            return;
        }
        biscuit.remIt(SENSITIVE_WORDS_COOKIE);
    },
};
